import { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const BudgetStep = ({ formData, onChange, errors }) => {
  const [budgetType, setBudgetType] = useState(formData?.budgetType || 'hourly');

  const handleBudgetTypeChange = (type) => {
    setBudgetType(type);
    onChange('budgetType', type);
    if (type === 'hourly') {
      onChange('fixedPrice', '');
    } else {
      onChange('hourlyRateMin', '');
      onChange('hourlyRateMax', '');
    }
  };

  const paymentTerms = [
    { value: 'upon-completion', label: 'Upon Completion' },
    { value: 'milestone', label: 'Milestone-based' },
    { value: 'upfront-50', label: '50% Upfront, 50% on Completion' },
    { value: 'weekly', label: 'Weekly Payment' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-success/10 border border-success/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl">💰</span>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">Budget Guidelines</h4>
            <p className="text-sm text-muted-foreground">
              Average rates in Manila: Plumbing ₱300-500/hr, Cleaning ₱200-350/hr, Tutoring ₱250-400/hr. Set competitive rates to attract quality workers.
            </p>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Budget Type <span className="text-error">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleBudgetTypeChange('hourly')}
            className={`
              p-4 md:p-6 rounded-md border-2 transition-smooth text-left
              ${budgetType === 'hourly' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }
            `}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-foreground">Hourly Rate</span>
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${budgetType === 'hourly' ? 'border-primary' : 'border-border'}
              `}>
                {budgetType === 'hourly' && (
                  <div className="w-3 h-3 rounded-full bg-primary" />
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Pay by the hour for ongoing or flexible work
            </p>
          </button>

          <button
            type="button"
            onClick={() => handleBudgetTypeChange('fixed')}
            className={`
              p-4 md:p-6 rounded-md border-2 transition-smooth text-left
              ${budgetType === 'fixed' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }
            `}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-foreground">Fixed Price</span>
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${budgetType === 'fixed' ? 'border-primary' : 'border-border'}
              `}>
                {budgetType === 'fixed' && (
                  <div className="w-3 h-3 rounded-full bg-primary" />
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Set a total project price for defined scope
            </p>
          </button>
        </div>
      </div>
      {budgetType === 'hourly' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Minimum Rate (₱/hour)"
            type="number"
            placeholder="200"
            value={formData?.hourlyRateMin || ''}
            onChange={(e) => onChange('hourlyRateMin', e?.target?.value)}
            error={errors?.hourlyRateMin}
            required
            min="100"
          />
          <Input
            label="Maximum Rate (₱/hour)"
            type="number"
            placeholder="500"
            value={formData?.hourlyRateMax || ''}
            onChange={(e) => onChange('hourlyRateMax', e?.target?.value)}
            error={errors?.hourlyRateMax}
            required
            min="100"
          />
        </div>
      ) : (
        <Input
          label="Fixed Price (₱)"
          type="number"
          placeholder="5000"
          value={formData?.fixedPrice || ''}
          onChange={(e) => onChange('fixedPrice', e?.target?.value)}
          error={errors?.fixedPrice}
          required
          min="500"
          description="Total amount for the entire project"
        />
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Payment Terms <span className="text-error">*</span>
        </label>
        <div className="space-y-2">
          {paymentTerms?.map((term) => (
            <label
              key={term?.value}
              className={`
                flex items-center gap-3 p-4 rounded-md border transition-smooth cursor-pointer
                ${formData?.paymentTerms === term?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
            >
              <input
                type="radio"
                name="paymentTerms"
                value={term?.value}
                checked={formData?.paymentTerms === term?.value}
                onChange={(e) => onChange('paymentTerms', e?.target?.value)}
                className="w-4 h-4 text-primary"
              />
              <span className="text-sm font-medium text-foreground">{term?.label}</span>
            </label>
          ))}
        </div>
        {errors?.paymentTerms && (
          <p className="text-sm text-error mt-2">{errors?.paymentTerms}</p>
        )}
      </div>
      <Input
        label="Estimated Hours (Optional)"
        type="number"
        placeholder="8"
        value={formData?.estimatedHours || ''}
        onChange={(e) => onChange('estimatedHours', e?.target?.value)}
        min="1"
        description="Help workers understand the scope of work"
      />
      <Checkbox
        label="I'm open to negotiating the rate with qualified workers"
        checked={formData?.negotiable || false}
        onChange={(e) => onChange('negotiable', e?.target?.checked)}
      />
    </div>
  );
};

export default BudgetStep;